import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import { FC, useState } from 'react'
import {
  AutocompleteInput,
  ReferenceInput,
  SaveButton,
  SimpleForm,
  Toolbar,
  required,
  useNotify,
  useRedirect,
  useTranslate,
} from 'react-admin'
import { getUrl, httpClient } from '../../providers/data'
import { Samenwerking } from '../../types/apiRequests'

interface TransferSamenwerkingDialogProps {
  record: Samenwerking
  disabled?: boolean
}

const TransferSamenwerkingDialog: FC<TransferSamenwerkingDialogProps> = ({ record, disabled = false }) => {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const translate = useTranslate()
  const notify = useNotify()
  const redirect = useRedirect()

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const save = (values: Record<string, unknown>) => {
    const organisatieId = values.organisatieId as string
    if (!organisatieId) return

    if (!window.confirm(translate('data.confirmTransferSamenwerking'))) return

    setIsLoading(true)
    httpClient(`${getUrl()}/samenwerkingen/${record.id}/transfer/${organisatieId}`, { method: 'PUT' })
      .then(() => {
        notify('data.transferSuccess', { type: 'success' })
        handleClose()
        redirect('/samenwerkingen')
      })
      .catch((error: unknown) => {
        const apiError = error as {
          message?: string
          body?: { detail?: string | object[]; message?: string }
        }
        const message =
          apiError?.body?.message ||
          apiError?.message ||
          (typeof apiError?.body?.detail === 'string' ? apiError?.body?.detail : null) ||
          'ra.notification.http_error'
        notify(message, { type: 'warning' })
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <>
      <SwapHorizIcon
        fontSize="small"
        onClick={disabled ? undefined : handleOpen}
        sx={{
          'color': disabled ? 'grey.400' : 'primary.main',
          'cursor': disabled ? 'default' : 'pointer',
          ':hover': disabled ? {} : { color: 'primary.dark' },
        }}
      />
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SwapHorizIcon />
          {translate('data.transferSamenwerking')}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('data.transferWarning')}
          </Alert>
          <SimpleForm
            onSubmit={save}
            toolbar={
              <Toolbar>
                <SaveButton disabled={isLoading} label="data.transferSamenwerking" />
                <Button onClick={handleClose}>{translate('ra.action.cancel')}</Button>
              </Toolbar>
            }
          >
            <ReferenceInput label="data.transferTo" source="organisatieId" reference="organisaties">
              <AutocompleteInput optionText="naam" optionValue="id" validate={required()} fullWidth blurOnSelect={false} />
            </ReferenceInput>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default TransferSamenwerkingDialog
